/**
 * <athlete-grid> — FND-793
 *
 * Two jobs:
 *   1. Reveal a "Read more" toggle only on bios that actually overflow their clamp.
 *   2. Append the next paginated page of athletes in place, via the Section
 *      Rendering API, without discarding what is already on screen.
 *
 * Note: a metaobject array paginates under a hashed query param (page_7a32002f=2),
 * not `page=`, so the next URL is always taken from Liquid's paginate.next.url
 * rather than reconstructed here.
 */
class AthleteGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-athlete-grid]');
    this.status = this.querySelector('[data-athlete-status]');
    this.sectionId = this.dataset.sectionId;

    this.setupToggles(this);
    this.bindLoadMore();

    // Re-measure clamps when fonts finish loading, otherwise overflow is
    // measured against a fallback face and some toggles never appear.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.setupToggles(this));
    }

    this.resizeHandler = this.debounce(() => this.setupToggles(this), 200);
    window.addEventListener('resize', this.resizeHandler);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  /**
   * Show the toggle only where the text is genuinely clipped. Cards already
   * expanded by the visitor are left alone.
   */
  setupToggles(scope) {
    scope.querySelectorAll('[data-athlete-card]').forEach((card) => {
      const bio = card.querySelector('[data-athlete-bio]');
      const toggle = card.querySelector('[data-athlete-toggle]');
      if (!bio || !toggle) return;

      if (bio.classList.contains('is-expanded')) return;

      const overflows = bio.scrollHeight > bio.clientHeight + 1;
      toggle.hidden = !overflows;

      if (overflows && !toggle.dataset.bound) {
        toggle.dataset.bound = 'true';
        toggle.addEventListener('click', () => this.toggleBio(bio, toggle));
      }
    });
  }

  toggleBio(bio, toggle) {
    const expanded = bio.classList.toggle('is-expanded');
    toggle.setAttribute('aria-expanded', String(expanded));

    const more = toggle.querySelector('[data-athlete-toggle-more]');
    const less = toggle.querySelector('[data-athlete-toggle-less]');
    if (more) more.hidden = expanded;
    if (less) less.hidden = !expanded;
  }

  bindLoadMore() {
    const link = this.querySelector('[data-athlete-more-link]');
    if (!link) return;

    link.addEventListener('click', (event) => {
      event.preventDefault();
      this.loadMore(link);
    });
  }

  async loadMore(link) {
    const url = link.getAttribute('href');
    if (!url || link.classList.contains('is-loading')) return;

    link.classList.add('is-loading');
    link.setAttribute('aria-busy', 'true');
    if (this.status) this.status.textContent = this.dataset.textLoading || '';

    try {
      const separator = url.indexOf('?') === -1 ? '?' : '&';
      const response = await fetch(`${url}${separator}section_id=${this.sectionId}`);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, 'text/html');

      const incoming = doc.querySelectorAll('[data-athlete-grid] > [data-athlete-card]');
      if (!incoming.length) throw new Error('No athletes in response');

      const added = [];
      incoming.forEach((card) => {
        const node = document.importNode(card, true);
        this.grid.appendChild(node);
        added.push(node);
      });

      // Move the button to the next page, or retire it on the last one.
      const nextLink = doc.querySelector('[data-athlete-more-link]');
      const footer = this.querySelector('[data-athlete-more]');
      if (nextLink && nextLink.getAttribute('href')) {
        link.setAttribute('href', nextLink.getAttribute('href'));
      } else if (footer) {
        footer.remove();
      }

      // Re-scan the whole grid: a freshly appended card is the scope root,
      // so querySelectorAll on it alone would never match itself.
      this.setupToggles(this);

      if (this.status) {
        this.status.textContent = this.dataset.textLoaded || '';
      }

      // Send focus to the first new card so keyboard users are not dumped
      // back at the top of the grid.
      const first = added[0];
      if (first) {
        first.setAttribute('tabindex', '-1');
        first.focus({ preventScroll: true });
      }
    } catch (error) {
      console.error('[athlete-grid] load more failed:', error);
      link.classList.remove('is-loading');
      link.removeAttribute('aria-busy');
      if (this.status) this.status.textContent = this.dataset.textError || '';
      // Leave the href intact so the button still works as a plain link.
      return;
    }

    link.classList.remove('is-loading');
    link.removeAttribute('aria-busy');
  }
}

if (!customElements.get('athlete-grid')) {
  customElements.define('athlete-grid', AthleteGrid);
}
